import React, { useState } from "react";
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import type {
  ConfirmationCardNode as ConfirmationCardNodeType,
  ConfirmationCardItem,
} from "@/lib/pharmacyFirst/types";
import type {
  AnswerRecord,
  ConfirmationCardAmendment,
} from "@/lib/pharmacyFirst/types/audit.types";

export interface ConfirmationCardResult {
  amendments: ConfirmationCardAmendment[];
  cannotProceedOutcomeId: string | null;
}

interface ConfirmationCardNodeProps {
  node: ConfirmationCardNodeType;
  auditAnswers: AnswerRecord[];
  isDarkMode?: boolean;
  stepNumber?: number;
  /** Previously given answer, re-seeded when the pharmacist steps back. */
  value?: ConfirmationCardResult | null;
  onAnswer: (result: ConfirmationCardResult) => void;
}

const DEFAULT_AMENDMENT_REASONS = [
  "Patient disclosed during consultation",
  "Record out of date",
  "Record incorrect",
  "Other (specify)",
];

function findStoredAnswer(
  auditAnswers: AnswerRecord[],
  sourceNodeId: string,
  sourceItemId?: string,
): AnswerRecord | null {
  let found: AnswerRecord | null = null;
  for (const record of auditAnswers) {
    if (record.nodeId === sourceNodeId) found = record;
  }

  if (sourceItemId !== undefined) {
    if (found === null) return null;
    const selected = Array.isArray(found.answer)
      ? (found.answer as string[]).includes(sourceItemId)
      : false;
    return { ...found, answer: selected ? "yes" : "no" };
  }

  return found;
}

function normaliseAnswer(raw: unknown): "yes" | "no" | null {
  if (raw === "yes" || raw === "no") return raw;
  if (typeof raw === "boolean") return raw ? "yes" : "no";
  if (
    raw !== null &&
    typeof raw === "object" &&
    "answer" in raw &&
    typeof (raw as any).answer === "boolean"
  ) {
    return (raw as any).answer ? "yes" : "no";
  }
  if (
    raw !== null &&
    typeof raw === "object" &&
    "answer" in raw &&
    (raw as any).answer === null
  ) {
    return "no";
  }
  return null;
}

function getOutcomeId(target: string | { outcomeId: string }): string {
  return typeof target === "string" ? target : target.outcomeId;
}

export function ConfirmationCardNode({
  node,
  auditAnswers,
  isDarkMode = false,
  stepNumber,
  value = null,
  onAnswer,
}: ConfirmationCardNodeProps) {
  const reasons = node.amendmentReasons ?? DEFAULT_AMENDMENT_REASONS;

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [amendedAnswer, setAmendedAnswer] = useState<Record<string, "yes" | "no">>(
    () =>
      Object.fromEntries(
        (value?.amendments ?? []).map((a) => [a.sourceNodeId, a.newAnswer])
      )
  );
  const [amendedReason, setAmendedReason] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (value?.amendments ?? []).map((a) => [a.sourceNodeId, a.reason])
    )
  );
  const [freshAnswer, setFreshAnswer] = useState<Record<string, "yes" | "no">>({});

  const itemState = (item: ConfirmationCardItem) => {
    const record = findStoredAnswer(auditAnswers, item.sourceNodeId, item.sourceItemId);
    const storedAnswer = record ? normaliseAnswer(record.answer) : null;
    const notFound = record === null;
    const amended = amendedAnswer[item.sourceNodeId];
    const effectiveAnswer = notFound
      ? freshAnswer[item.sourceNodeId] ?? null
      : amended ?? storedAnswer;
    const isAmended = !notFound && amended !== undefined && amended !== storedAnswer;
    const hasReason = (amendedReason[item.sourceNodeId] ?? "").length > 0;
    const confirmed = !isAmended && effectiveAnswer === item.safeAnswer && !notFound;
    const resolved =
      confirmed || (isAmended && hasReason) || (notFound && effectiveAnswer !== null);
    return {
      record,
      storedAnswer,
      effectiveAnswer,
      isAmended,
      hasReason,
      notFound,
      confirmed,
      resolved,
    };
  };

  const allResolved = node.items.every((item) => itemState(item).resolved);

  const handleConfirm = () => {
    if (!allResolved) return;

    const amendments: ConfirmationCardAmendment[] = [];
    let cannotProceedOutcomeId: string | null = null;

    for (const item of node.items) {
      const state = itemState(item);
      if (state.isAmended) {
        const newAnswer = amendedAnswer[item.sourceNodeId];
        amendments.push({
          nodeId: node.id,
          sourceNodeId: item.sourceNodeId,
          questionText: item.questionText,
          originalAnswer: state.record ? state.record.answer : null,
          newAnswer,
          reason: amendedReason[item.sourceNodeId] ?? "",
          amendedAt: new Date().toISOString(),
        });
      }
      if (
        cannotProceedOutcomeId === null &&
        state.effectiveAnswer !== item.safeAnswer
      ) {
        cannotProceedOutcomeId = getOutcomeId(item.cannotProceed);
      }
    }

    onAnswer({ amendments, cannotProceedOutcomeId });
  };

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-4">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${isDarkMode ? "bg-white text-black" : "bg-black text-white"}`}
          >
            {stepNumber}
          </div>
        )}
        <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
          {node.heading}
        </h3>
      </div>

      <div className="space-y-2">
        {node.items.map((item) => {
          const state = itemState(item);
          const isOpen = expanded[item.sourceNodeId] === true;

          return (
            <div
              key={item.sourceNodeId}
              className={`border rounded-lg ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <div className="flex items-start justify-between gap-3 p-3">
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                    {item.questionText}
                  </p>
                  <div className="mt-1 flex items-center gap-2">
                    {state.notFound ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        No previous answer found
                      </span>
                    ) : (
                      <>
                        <span className={`text-xs font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>
                          {state.isAmended ? "Amended to:" : "Answer:"}{" "}
                          {state.effectiveAnswer === "yes" ? "Yes" : state.effectiveAnswer === "no" ? "No" : "—"}
                        </span>
                        {state.confirmed && (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        )}
                        {state.isAmended && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Amended
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {state.notFound && (
                    <div className="mt-2">
                      <p className={`text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                        Answer
                      </p>
                      <div className="flex gap-2">
                        {(["yes", "no"] as const).map((opt) => {
                          const selected = freshAnswer[item.sourceNodeId] === opt;
                          return (
                            <button
                              key={opt}
                              type="button"
                              aria-pressed={selected}
                              onClick={() =>
                                setFreshAnswer((p) => ({ ...p, [item.sourceNodeId]: opt }))
                              }
                              className={`h-9 w-16 inline-flex items-center justify-center rounded-md border text-sm font-medium transition select-none ${
                                selected
                                  ? isDarkMode
                                    ? "border-blue-500 bg-blue-950/40 text-blue-100"
                                    : "border-blue-500 bg-blue-50 text-blue-900"
                                  : isDarkMode
                                    ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
                                    : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                              }`}
                            >
                              {opt === "yes" ? "Yes" : "No"}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {!state.notFound && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((p) => ({ ...p, [item.sourceNodeId]: !isOpen }))
                    }
                    className={`inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline shrink-0 ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                  >
                    Amend
                    {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>

              {isOpen && !state.notFound && (
                <div className={`border-t px-3 py-3 space-y-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                  <div>
                    <p className={`text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                      Corrected answer
                    </p>
                    <div className="flex gap-2">
                      {(["yes", "no"] as const).map((opt) => {
                        const selected = amendedAnswer[item.sourceNodeId] === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            aria-pressed={selected}
                            onClick={() =>
                              setAmendedAnswer((p) => ({ ...p, [item.sourceNodeId]: opt }))
                            }
                            className={`h-9 w-16 inline-flex items-center justify-center rounded-md border text-sm font-medium transition select-none ${
                              selected
                                ? isDarkMode
                                  ? "border-blue-500 bg-blue-950/40 text-blue-100"
                                  : "border-blue-500 bg-blue-50 text-blue-900"
                                : isDarkMode
                                  ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
                                  : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                            }`}
                          >
                            {opt === "yes" ? "Yes" : "No"}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className={`text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                      Reason for amendment
                    </p>
                    <select
                      value={amendedReason[item.sourceNodeId] ?? ""}
                      onChange={(e) =>
                        setAmendedReason((p) => ({ ...p, [item.sourceNodeId]: e.target.value }))
                      }
                      className={`w-full text-sm rounded-lg border px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${isDarkMode
                        ? "border-gray-600 bg-gray-700 text-gray-100"
                        : "border-gray-300 bg-white text-gray-900"
                      }`}
                    >
                      <option value="">Select a reason…</option>
                      {reasons.map((reason) => (
                        <option key={reason} value={reason}>
                          {reason}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <FlowPrimaryButton
        label="Confirm all"
        wrapperClassName="mt-4"
        className="w-full"
        onClick={handleConfirm}
        disabled={!allResolved}
        blockedReason="Resolve every item to continue."
      />
    </div>
  );
}
